import { CheckIcon } from "@heroicons/react/24/solid";
import { cx } from "cva";
import {
  SubscriptionInterval,
  SubscriptionName,
} from "~/types/endil/subscription";
import { Button, Headline, Icon, Pill } from "~/ui-library";

const selectedSubButtonStyle = cx([
  "w-full",
  "bg-primary-100 border-primary",
  "text-primary text-body-small font-semibold",
]);
const deselectedSubButtonStyle = cx([
  "w-full",
  "bg-white border-gray-300",
  "text-gray-700 text-body-small font-regular",
]);

export type SubCheckoutSelectedSubscriptionProps = {
  selectedInterval: SubscriptionInterval;
  headline: string;
  subhead: string;
  sellingPoints: string[];
  changePlanToName: SubscriptionName;
  onPlanChange?: (newPlanName: SubscriptionName) => void;
  annualPerMonthPrice: number;
  annualDiscountPercentage: number;
  monthlyPerMonthPrice: number;
  subscriptionPlanFooterText: string;
  subtotal: string;
  dueToday: string;
  orderSummaryFooterText: string;
};

export const SubCheckoutSelectedSubscription: React.FC<
  SubCheckoutSelectedSubscriptionProps
> = ({
  selectedInterval,
  headline,
  subhead,
  sellingPoints,
  changePlanToName,
  onPlanChange,
  annualPerMonthPrice,
  annualDiscountPercentage,
  monthlyPerMonthPrice,
  subscriptionPlanFooterText,
  subtotal,
  dueToday,
  orderSummaryFooterText,
}) => (
  <>
    <div className="border-b-1 border-gray-300 px-m py-s">
      <div className="mb-s text-body-xsmall font-semibold">Selected</div>
      <Headline as="h5" level={5} className="mb-xs">
        {headline}
      </Headline>
      <div className="mb-s text-body-small">{subhead}</div>
      <ul className="mb-m">
        {sellingPoints.map((text, index) => (
          <li key={index} className="mb-xxs text-body-small last:mb-0">
            <Icon
              className="mr-s size-s fill-primary stroke-primary"
              SvgIcon={CheckIcon}
            />
            {text}
          </li>
        ))}
      </ul>
      <div className="mb-s text-body-xsmall font-semibold">
        Subscription Plan
      </div>

      <Button
        variant="outline"
        className={cx([
          selectedInterval === SubscriptionInterval.ANNUAL
            ? selectedSubButtonStyle
            : deselectedSubButtonStyle,
          "mb-xs",
        ])}
        onClick={
          selectedInterval !== SubscriptionInterval.ANNUAL && onPlanChange
            ? () => onPlanChange(changePlanToName)
            : undefined
        }
      >
        <div className="grid grid-cols-[min-content,min-content,1fr] items-center justify-start gap-s">
          <div>Annual</div>
          <div className="font-regular">${annualPerMonthPrice}/month</div>
          <div className="grid justify-items-end">
            <Pill appearance="success" className="leading-[1.2]">
              <span className="text-body-small font-regular">
                save {annualDiscountPercentage}%
              </span>
            </Pill>
          </div>
        </div>
      </Button>
      <Button
        variant="outline"
        className={cx([
          selectedInterval === SubscriptionInterval.MONTHLY
            ? selectedSubButtonStyle
            : deselectedSubButtonStyle,
          "mb-s",
        ])}
        onClick={
          selectedInterval !== SubscriptionInterval.MONTHLY && onPlanChange
            ? () => onPlanChange(changePlanToName)
            : undefined
        }
      >
        <div className="grid grid-cols-[min-content,min-content,1fr] items-center justify-start gap-s">
          <div>Monthly</div>
          <div className="font-regular">${monthlyPerMonthPrice}/month</div>
        </div>
      </Button>
      <div className="text-body-small">{subscriptionPlanFooterText}</div>
    </div>
    <div className="px-m py-s">
      <div className="mb-s text-headline6 font-semibold uppercase leading-headline6 tracking-headline6-block">
        Order Summary
      </div>
      <div className="mb-xs grid grid-cols-2 border-b-1 border-gray-300 pb-xs text-body-small">
        <div>Subtotal</div>
        <div className="text-right">{subtotal}</div>
      </div>
      <div className="mb-s grid grid-cols-2 font-semibold">
        <div>Due Today</div>
        <div className="text-right">{dueToday}</div>
      </div>
      <div className="text-body-xsmall">{orderSummaryFooterText}</div>
    </div>
  </>
);
